import { FCC } from "@/types/common";
import { Stack, Box, Modal, Typography, styled, Grid } from "@mui/material";
import { IconClose, IconSave } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { CaseMatchInfoForm } from "@/features/case/components/CaseEdit/CaseMatchInfoForm";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { convertResToFormData } from "@/features/talent/utils/convertTalentFormData";
import { useTalentInfo } from "@/api/services/main/talent";
import { useRef, useState, useMemo } from "react";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { Modal as ConfirmModal } from "@/components/elements/Modal";

type TCaseMatchInfoModalProps = {
  isOpen: boolean;
  caseMatch?: TCaseMatches;
  currentStatus?: number;
  onClose: () => void;
  onSuccess?: () => void;
  canEdit?: boolean;
  exemptSpecifiedSkillCert?: boolean;
};

export const CaseMatchInfoModal: FCC<TCaseMatchInfoModalProps> = ({
  isOpen,
  caseMatch,
  currentStatus,
  onClose,
  onSuccess,
  canEdit = true,
  exemptSpecifiedSkillCert = false,
}) => {
  const talentId = caseMatch?.talent?.id;
  const { data: talentData, isLoading: isTalentLoading } = useTalentInfo(talentId);
  const talentFormData = useMemo(() => (talentData?.data ? convertResToFormData(talentData.data) : undefined), [talentData?.data]);

  const isLoading = isOpen && caseMatch && talentId && (isTalentLoading || !talentFormData);

  const formRef = useRef<{ submit: () => void; isLoading: boolean; isDirty: boolean }>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);

  const handleLoadingChange = (isLoading: boolean) => {
    setIsSubmitting(isLoading);
  };

  const handleSave = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handleSuccess = () => {
    onSuccess?.();
  };

  const handleCloseAttempt = () => {
    if (formRef.current?.isDirty) {
      setShowConfirmLeave(true);
    } else {
      onClose();
    }
  };

  const handleConfirmLeave = () => {
    setShowConfirmLeave(false);
    onClose();
  };

  const handleCancelLeave = () => {
    setShowConfirmLeave(false);
  };

  return (
    <>
      <Modal
        open={isOpen}
        hideBackdrop={false}
        onClose={(_, reason) => {
          // Prevent closing modal when clicking outside (backdrop)
          if (reason === "backdropClick") return;
          handleCloseAttempt();
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 0,
          "& .MuiBackdrop-root": {
            padding: 0,
          },
        }}
      >
        <ModalContainer>
          <Stack
            direction="row"
            bgcolor={background.disable}
            height="56px"
            minHeight="56px"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
          >
            <Typography variant="body14Light" color={"#0B0C0D"}>
              マッチング情報
            </Typography>
            <IconBox onClick={handleCloseAttempt} sx={{ p: 0 }}>
              <IconClose color={black[80]} fontSize={24} />
            </IconBox>
          </Stack>

          <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}>
            {isLoading ? (
              <LoadingArea />
            ) : (
              <Stack sx={{ flex: 1, overflowY: "auto", px: 4, pb: 4, pt: 1 }}>
                {caseMatch ? (
                  <Grid container spacing={4}>
                    <Grid item>
                      <CaseMatchInfoForm
                        ref={formRef}
                        caseMatch={caseMatch}
                        talent={talentFormData}
                        currentStatus={currentStatus}
                        onSuccess={handleSuccess}
                        onLoadingChange={handleLoadingChange}
                        canEdit={canEdit}
                        exemptSpecifiedSkillCert={exemptSpecifiedSkillCert}
                      />
                    </Grid>
                  </Grid>
                ) : null}
              </Stack>
            )}
          </Box>

          <Box>
            <hr color={divider.high} />
            <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px">
              <Button variant="ghost" size="sm" onClick={handleCloseAttempt} sx={{ width: "102px" }}>
                キャンセル
              </Button>
              <Button variant="primary" size="sm" startIcon={<IconSave />} onClick={handleSave} isLoading={isSubmitting} sx={{ width: "88px" }}>
                保存
              </Button>
            </Stack>
          </Box>
        </ModalContainer>
      </Modal>

      <ConfirmModal isOpen={showConfirmLeave} onClose={handleCancelLeave} onOk={handleConfirmLeave} labelClose="いいえ" labelOk="はい" width={500}>
        <Stack mb={2}>
          <Typography>入力が完了していません。このまま戻ってもよろしいですか？</Typography>
        </Stack>
      </ConfirmModal>
    </>
  );
};

const ModalContainer = styled(Box)`
  width: 764px;
  height: 728px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  margin: auto;
  &:focus {
    outline: none;
  }
`;
